/* Atelier64 — Emblema 3D (three.js, carregado só quando a seção aparece) */
const THREE_URL = 'three';
const SVG_URL = 'three/addons/loaders/SVGLoader.js';
const ENV_URL = 'three/addons/environments/RoomEnvironment.js';

function hasWebGL() {
  try { const c = document.createElement('canvas'); return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl'))); } catch (e) { return false; }
}

async function mount(section) {
  const host = section.querySelector('.a64-emblem__canvas');
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!hasWebGL()) { host.classList.add('is-fallback'); return; }

  let THREE, SVGLoader, RoomEnvironment;
  try {
    [THREE, { SVGLoader }, { RoomEnvironment }] = await Promise.all([
      import(THREE_URL), import(SVG_URL), import(ENV_URL)
    ]);
  } catch (e) { host.classList.add('is-fallback'); return; }

  const svgText = await fetch(section.dataset.svg).then(r => r.text()).catch(() => null);
  if (!svgText) { host.classList.add('is-fallback'); return; }

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  host.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(renderer), 0.04).texture;

  const camera = new THREE.PerspectiveCamera(30, 1, 0.1, 100);
  camera.position.set(0, 0, 9);

  // Geometria extrudada a partir do SVG da logo
  const data = new SVGLoader().parse(svgText);
  const shapes = [];
  data.paths.forEach(p => SVGLoader.createShapes(p).forEach(s => shapes.push(s)));
  const geo = new THREE.ExtrudeGeometry(shapes, { depth: 70, bevelEnabled: true, bevelThickness: 10, bevelSize: 6, bevelSegments: 6, curveSegments: 24 });
  geo.center();
  geo.computeBoundingBox();
  const size = new THREE.Vector3(); geo.boundingBox.getSize(size);
  const k = 4.2 / size.x;
  geo.scale(k, -k, k); // SVG tem o eixo Y invertido
  geo.computeVertexNormals();

  const mat = new THREE.MeshPhysicalMaterial({ color: 0xd9dcd6, metalness: 1, roughness: 0.22, clearcoat: 1, clearcoatRoughness: 0.08 });
  const edgeMat = new THREE.MeshPhysicalMaterial({ color: 0xa7c4b0, metalness: 1, roughness: 0.3 });
  const mesh = new THREE.Mesh(geo, [mat, edgeMat]);
  const group = new THREE.Group(); group.add(mesh); scene.add(group);
  section.a64Rotation = () => group.rotation.y;

  // Luz de recorte verde (cor da marca) e uma luz quente
  const rim = new THREE.PointLight(0xa7c4b0, 40, 20); rim.position.set(-4, 2, -3); scene.add(rim);
  const key = new THREE.DirectionalLight(0xffffff, 1.2); key.position.set(3, 4, 6); scene.add(key);

  // Anel orbital fino
  const ring = new THREE.Mesh(new THREE.TorusGeometry(2.9, 0.006, 8, 220), new THREE.MeshBasicMaterial({ color: 0x3a3a3a }));
  ring.rotation.x = Math.PI / 2.3; scene.add(ring);

  function resize() {
    const w = host.clientWidth, h = host.clientHeight;
    renderer.setSize(w, h, false); camera.aspect = w / h; camera.updateProjectionMatrix();
    camera.position.z = w < 600 ? 11 : 9;
  }
  resize(); window.addEventListener('resize', resize);

  // Arrastar para girar, com inércia; mouse inclina levemente
  let rotY = -0.5, rotX = 0.12, velY = reduce ? 0 : 0.004, velX = 0, dragging = false, lx = 0, ly = 0, tiltX = 0, tiltY = 0, lastTouch = -1e9;
  const el = renderer.domElement;
  el.style.touchAction = 'pan-y';
  el.addEventListener('pointerdown', e => { e.preventDefault(); dragging = true; lastTouch = performance.now(); lx = e.clientX; ly = e.clientY; el.setPointerCapture(e.pointerId); host.classList.add('is-grabbing'); });
  el.addEventListener('pointermove', e => {
    const r = el.getBoundingClientRect();
    tiltX = ((e.clientY - r.top) / r.height - 0.5) * 0.25; tiltY = ((e.clientX - r.left) / r.width - 0.5) * 0.35;
    if (!dragging) return;
    velY = (e.clientX - lx) * 0.01; velX = (e.clientY - ly) * 0.006; lx = e.clientX; ly = e.clientY; lastTouch = performance.now();
    rotY += velY; rotX = Math.max(-0.6, Math.min(0.6, rotX + velX));
  });
  const up = () => { dragging = false; host.classList.remove('is-grabbing'); };
  el.addEventListener('pointerup', up); el.addEventListener('pointercancel', up);
  el.addEventListener('pointerleave', () => { tiltX = 0; tiltY = 0; });

  let visible = true;
  new IntersectionObserver(([e]) => { visible = e.isIntersecting; }).observe(host);

  const clock = new THREE.Clock();
  (function loop() {
    requestAnimationFrame(loop);
    if (!visible) return;
    const t = clock.getElapsedTime();
    if (!dragging) { const idle = performance.now() - lastTouch > 2500; velY += (((reduce || !idle) ? 0 : 0.004) - velY) * (idle ? 0.02 : 0.06); rotY += velY; if (idle) rotX += (0.12 - rotX) * 0.02; }
    group.rotation.y = rotY + tiltY;
    group.rotation.x = rotX + tiltX;
    group.position.y = reduce ? 0 : Math.sin(t * 0.8) * 0.06;
    ring.rotation.z = t * 0.05;
    rim.position.x = Math.sin(t * 0.6) * 5;
    renderer.render(scene, camera);
  })();
  host.classList.add('is-ready');
}

const sections = document.querySelectorAll('[data-a64-emblem]');
if (sections.length) {
  const io = new IntersectionObserver(entries => {
    entries.forEach(e => { if (e.isIntersecting) { io.unobserve(e.target); mount(e.target); } });
  }, { rootMargin: '300px 0px' });
  sections.forEach(s => io.observe(s));
}
